import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  DatePicker,
  Popconfirm,
  Tooltip,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Image,
  Select,
  App
} from 'antd';
import CompactPagination from '../components/CompactPagination';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DragOutlined
} from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import advertisementService from '../services/advertisementService';
import { getPublicImageUrl } from '../../utils/imageUtils';
import SmartImage from '../../components/common/SmartImage';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Advertisements = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAdvertisement, setEditingAdvertisement] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    isActive: undefined,
    search: ''
  });

  // Etat local pour générateur de dégradé
  const [gradientStart, setGradientStart] = useState('#1e40af');
  const [gradientEnd, setGradientEnd] = useState('#aeb4be');
  const [gradientAngle, setGradientAngle] = useState(135);

  const updateGradientBackground = (start, end, angle) => {
    const s = start || gradientStart;
    const e = end || gradientEnd;
    const a = typeof angle === 'number' ? angle : gradientAngle;
    const css = `linear-gradient(${a}deg, ${s} 0%, ${e} 100%)`;
    form.setFieldsValue({ background: css });
  };

  const tryInitGradientFromCss = (css) => {
    if (!css || typeof css !== 'string') return;
    const match = css.match(/linear-gradient\(([-\d.]+)deg,\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]*\))\s*0%?,\s*(#[0-9a-fA-F]{3,8}|rgb\([^)]*\))\s*100%\)/);
    if (match) {
      const angle = parseFloat(match[1]);
      const start = match[2];
      const end = match[3];
      if (!Number.isNaN(angle)) setGradientAngle(angle);
      if (start) setGradientStart(start);
      if (end) setGradientEnd(end);
    }
  };

  useEffect(() => {
    loadAdvertisements();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadAdvertisements = async () => {
    setLoading(true);
    try {
      const response = await advertisementService.getAdvertisements({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      });
      
      setAdvertisements(response.advertisements);
      setPagination(prev => ({
        ...prev,
        total: response.total
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des publicités:', error);
      message.error('Erreur lors du chargement des publicités');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAdvertisement(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (advertisement) => {
    setEditingAdvertisement(advertisement);
    form.setFieldsValue({
      ...advertisement,
      dateRange: (advertisement.startDate || advertisement.endDate)
        ? [
            advertisement.startDate ? dayjs(advertisement.startDate) : null,
            advertisement.endDate ? dayjs(advertisement.endDate) : null
          ]
        : null
    });
    tryInitGradientFromCss(advertisement.background);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await advertisementService.deleteAdvertisement(id);
      message.success('Publicité supprimée avec succès');
      loadAdvertisements();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      message.error('Erreur lors de la suppression de la publicité');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await advertisementService.toggleAdvertisementStatus(id);
      message.success('Statut mis à jour avec succès');
      loadAdvertisements();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const advertisementData = {
        ...values,
        startDate: values.dateRange?.[0]
          ? (values.dateRange[0].toDate ? values.dateRange[0].toDate().toISOString() : new Date(values.dateRange[0]).toISOString())
          : undefined,
        endDate: values.dateRange?.[1]
          ? (values.dateRange[1].toDate ? values.dateRange[1].toDate().toISOString() : new Date(values.dateRange[1]).toISOString())
          : undefined
      };
      delete advertisementData.dateRange;

      if (editingAdvertisement) {
        await advertisementService.updateAdvertisement(editingAdvertisement.id, advertisementData);
        message.success('Publicité mise à jour avec succès');
      } else {
        await advertisementService.createAdvertisement(advertisementData);
        message.success('Publicité créée avec succès');
      }

      setModalVisible(false);
      loadAdvertisements();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde de la publicité');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(advertisements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setAdvertisements(items);

    try {
      const reorderedAdvertisements = items.map((item, index) => ({
        id: item.id,
        order: index
      }));
      
      await advertisementService.reorderAdvertisements(reorderedAdvertisements);
      message.success('Ordre mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la réorganisation:', error);
      message.error('Erreur lors de la réorganisation');
      loadAdvertisements(); // Recharger en cas d'erreur
    }
  };

  const columns = [
       {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 160,
      render: (image) => {
        return (
          <div style={{
            width: 140,
            aspectRatio: '3/1',
            borderRadius: 6,
            overflow: 'hidden',
            background: '#0f172a',
            border: '1px solid #e2e8f0'
          }}>
            <SmartImage
              src={image}
              alt="Publicité"
              imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        );
      }
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Actif' : 'Inactif'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? 'Désactiver' : 'Activer'}>
            <Button
              type={record.isActive ? 'default' : 'primary'}
              shape="circle"
              icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => handleToggleStatus(record.id)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              shape="circle"
              icon={<DeleteOutlined />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <h2>Gestion des Publicités</h2>
            </Col>
            <Col>
              <Space>
                <Select
                  placeholder="Filtrer par statut"
                  style={{ width: 150 }}
                  value={filters.isActive}
                  onChange={(value) => setFilters(prev => ({ ...prev, isActive: value }))}
                  allowClear
                >
                  <Select.Option value={true}>Actif</Select.Option>
                  <Select.Option value={false}>Inactif</Select.Option>
                </Select>
                <Input.Search
                  placeholder="Rechercher..."
                  style={{ width: 200 }}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onSearch={() => loadAdvertisements()}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  Nouvelle Publicité
                </Button>
              </Space>
            </Col>
          </Row>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="advertisements">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <Table
                    columns={columns}
                    dataSource={advertisements}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    components={{
                      body: {
                        row: (props) => (
                          <Draggable draggableId={props['data-row-key']} index={props.index}>
                            {(provided) => (
                              <tr
                                {...props}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {props.children}
                              </tr>
                            )}
                          </Draggable>
                        )
                      }
                    }}
                  />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          
          {/* Pagination compacte */}
          {pagination.total > pagination.pageSize && (
            <CompactPagination
              current={pagination.current}
              total={pagination.total}
              pageSize={pagination.pageSize}
              onChange={(page, pageSize) => setPagination({ ...pagination, current: page, pageSize })}
              onShowSizeChange={(current, size) => setPagination({ ...pagination, current: 1, pageSize: size })}
              showSizeChanger={true}
              showQuickJumper={false}
              showTotal={true}
              size="small"
            />
          )}
        </Card>

        <Modal
          title={editingAdvertisement ? 'Modifier la publicité' : 'Nouvelle publicité'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >


            <Form.Item
              name="image"
              label="Image Desktop (1200 x 400 px)"
              rules={[{ required: true, message: 'L\'image est requise' }]}
            >
              <Input
                placeholder="URL de l'image desktop (Google Drive ou autre)"
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      const imageUrl = form.getFieldValue('image');
                      if (imageUrl) {
                        const convertedUrl = getPublicImageUrl(imageUrl);
                        window.open(convertedUrl, '_blank');
                      } else {
                        message.warning('Veuillez d\'abord saisir une URL d\'image');
                      }
                    }}
                  >
                    Tester
                  </Button>
                }
              />
            </Form.Item>

            {/* Preview desktop */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.image !== cur.image}>
              {() => {
                const url = form.getFieldValue('image');
                if (!url) return null;
                const src = getPublicImageUrl(url);
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Aperçu Desktop (3:1)</div>
                    <div style={{
                      width: '100%',
                      aspectRatio: '3/1',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      background: '#0f172a'
                    }}>
                      <img
                        src={src}
                        alt="Preview desktop"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                );
              }}
            </Form.Item>

            <Form.Item
              name="imageMobile"
              label="Image Mobile (600 x 400 px)"
            >
              <Input
                placeholder="URL de l'image mobile (Google Drive ou autre)"
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      const imageUrl = form.getFieldValue('imageMobile');
                      if (imageUrl) {
                        const convertedUrl = getPublicImageUrl(imageUrl);
                        window.open(convertedUrl, '_blank');
                      } else {
                        message.warning('Veuillez d\'abord saisir une URL d\'image mobile');
                      }
                    }}
                  >
                    Tester
                  </Button>
                }
              />
            </Form.Item>

            {/* Preview mobile */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.imageMobile !== cur.imageMobile}>
              {() => {
                const url = form.getFieldValue('imageMobile');
                if (!url) return null;
                const src = getPublicImageUrl(url);
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Aperçu Mobile (3:2)</div>
                    <div style={{
                      width: 200,
                      aspectRatio: '3/2',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      background: '#0f172a'
                    }}>
                      <img
                        src={src}
                        alt="Preview mobile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                );
              }}
            </Form.Item>

            {/* Suppression du champ de dégradé */}

            {/* Suppression ordre/statut */}

            {/* Suppression période */}

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setModalVisible(false)}>
                  Annuler
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingAdvertisement ? 'Mettre à jour' : 'Créer'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default Advertisements;
